import {
  type NotificationSeverity,
  NotificationSeverityLevel,
  notificationSeveritySchema,
} from '@kirin/api-client/contracts/notification-settings';
import { safeLocalStorage } from './safeLocalStorage';
import type {
  LocalStorageSchema,
  PersistedNotificationSeverity,
} from './safeLocalStorage/schema';

export const LAST_NOTIFICATION_SEVERITY_KEY = 'ui.last-notification-severity';

type LastSavedSeverities =
  LocalStorageSchema[typeof LAST_NOTIFICATION_SEVERITY_KEY];

// Runtime narrowing for hand-edited / migration-corrupted blobs. The schema
// itself allows `none`, but the persisted "last picked" slot must never store
// the off state — `setLastNotificationSeverity` guards the write path; this
// guards the read path symmetrically.
const isPersistedSeverity = (
  value: unknown
): value is PersistedNotificationSeverity => {
  const parsed = notificationSeveritySchema.safeParse(value);
  return parsed.success && parsed.data !== NotificationSeverityLevel.None;
};

// safeLocalStorage.getItem only JSON.parses the raw string — nothing enforces
// shape or value-validity. Coerce missing fields and filter values that aren't
// valid persisted severities so a poisoned blob can't reach the mutation layer.
const readLastSavedSeverities = (): LastSavedSeverities => {
  const raw = safeLocalStorage.getItem(LAST_NOTIFICATION_SEVERITY_KEY);
  const personal = isPersistedSeverity(raw?.personal) ? raw.personal : null;
  const orgsIn: Record<string, unknown> = raw?.orgs ?? {};
  const orgs: Record<string, PersistedNotificationSeverity> = {};
  for (const [orgId, value] of Object.entries(orgsIn)) {
    if (isPersistedSeverity(value)) {
      orgs[orgId] = value;
    }
  }
  return { personal, orgs };
};

/**
 * Read the last non-`none` severity the user picked for the given context.
 * `organizationId === undefined` reads the personal slot; otherwise reads
 * `orgs[organizationId]`. Returns `null` when nothing has been saved for the
 * requested slot.
 */
export const getLastNotificationSeverity = (
  organizationId: string | undefined
): PersistedNotificationSeverity | null => {
  const saved = readLastSavedSeverities();
  if (organizationId === undefined) return saved.personal;
  return saved.orgs[organizationId] ?? null;
};

/**
 * Save the user's last-picked non-`none` severity so toggling off → on can
 * restore it. `'none'` is the off state itself and is a no-op here — the prior
 * saved value survives a toggle-off.
 */
export const setLastNotificationSeverity = (
  organizationId: string | undefined,
  severity: NotificationSeverity
): void => {
  if (severity === NotificationSeverityLevel.None) return;
  const narrowed: PersistedNotificationSeverity = severity;
  const prev = readLastSavedSeverities();
  const next: LastSavedSeverities =
    organizationId === undefined
      ? { ...prev, personal: narrowed }
      : { ...prev, orgs: { ...prev.orgs, [organizationId]: narrowed } };
  safeLocalStorage.setItem(LAST_NOTIFICATION_SEVERITY_KEY, next);
};
